import React, { useState } from 'react';
import styled from 'styled-components';
import CmsPopup from '../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../components/CmsPopupBtn';
import CmsPopupBtn from '../../../components/CmsPopupBtn';
import Modal from '../../../components/Modal';
import { AppTextField } from '../../../customComponents/AppTextField';
import { AppNumberTextField } from '../../../customComponents/AppNumberTextField';
import { AppEmailTextField } from '../../../customComponents/AppEmailTextField';
import { AppGPhoneTextField } from '../../../customComponents/AppGPhoneTextField';

// 입찰자 폼 데이터 타입
export interface BidderFormData {
  bidAmount: string;
  bidNumber: string;
  name: string;
  phone: string;
  email: string;
  memo: string;
}

interface BidderFormPopupProps {
  isOpen: boolean;
  isEditMode: boolean;
  formData: BidderFormData;
  formErrors: Partial<BidderFormData>;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  onInputChange: (field: keyof BidderFormData, value: string) => void;
}

// Styled Components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  
  & > * {
    flex: 1;
  }
`;

const BidderFormPopup: React.FC<BidderFormPopupProps> = ({
  isOpen,
  isEditMode,
  formData,
  formErrors,
  onClose,
  onSave,
  onDelete,
  onInputChange,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // 삭제 확인 모달 열기
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  // 삭제 확인
  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete();
    }
  };

  // 삭제 취소
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // 전화번호 변경 핸들러 (AppGPhoneTextField용)
  const handlePhoneChange = (value: string, isValid: boolean) => {
    onInputChange('phone', value);
  };

  const renderForm = () => (
    <FormContainer>
      {/* 등록 모드일 때는 입찰가, 입찰번호만 표시 */}
      {!isEditMode && (
        <>
          <AppNumberTextField
            value={formData.bidAmount}
            onChange={(e) => onInputChange('bidAmount', e.target.value)}
            label="*입찰가"
            placeholder="입찰가를 입력해주세요"
            errorMessage={formErrors.bidAmount}
          />

          <AppNumberTextField
            value={formData.bidNumber}
            onChange={(e) => onInputChange('bidNumber', e.target.value)}
            label="*현장번호"
            placeholder="현장번호를 입력해주세요"
            errorMessage={formErrors.bidNumber}
          />
        </>
      )}

      {/* 수정 모드일 때는 모든 필드 표시 */}
      {isEditMode && (
        <>
          <AppNumberTextField
            value={formData.bidAmount}
            onChange={(e) => onInputChange('bidAmount', e.target.value)}
            label="*입찰가"
            placeholder="입찰가를 입력해주세요"
            errorMessage={formErrors.bidAmount}
          />

          <AppNumberTextField
            value={formData.bidNumber}
            onChange={(e) => onInputChange('bidNumber', e.target.value)}
            label="*현장번호"
            placeholder="현장번호를 입력해주세요"
            errorMessage={formErrors.bidNumber}
          />

          <AppTextField
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            label="*이름"
            placeholder="이름을 입력해주세요"
            errorMessage={formErrors.name}
          />

          <AppGPhoneTextField
            value={formData.phone}
            onChange={handlePhoneChange}
            label="*전화번호"
            errorMessage={formErrors.phone}
          />

          <AppEmailTextField
            value={formData.email}
            onChange={(e) => onInputChange('email', e.target.value)}
            label="*이메일"
            errorMessage={formErrors.email}
            showValidationMessage={false}
          />

          <AppTextField
            value={formData.memo}
            onChange={(e) => onInputChange('memo', e.target.value)}
            label="비고"
            placeholder="비고를 입력해주세요"
            errorMessage={formErrors.memo}
            multiline
            height="150px"
          />
        </>
      )}
    </FormContainer>
  );

  return (
    <CmsPopup
      title={isEditMode ? '입찰자 정보 수정' : '현장 입찰자 등록'}
      isOpen={isOpen}
      onClose={onClose}
      width="600px"
      showRequiredMark={true}
      leftButtons={[
        ...(isEditMode ? [{
          label: '삭제',
          onClick: handleDeleteClick,
          type: CmsPopupBtnType.PRIMARY,
        }] : [])
      ]}
      rightButtons={[
        {
          label: isEditMode ? '수정' : '등록',
          onClick: onSave,
          type: CmsPopupBtnType.PRIMARY,
        },
        {
          label: '취소',
          onClick: onClose,
          type: CmsPopupBtnType.SECONDARY,
        },
      ]}
    >
      {renderForm()}
      
      {/* 삭제 확인 모달 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        header="입찰자 삭제"
        body={`정말로 "${formData.name || '이 입찰자'}"를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`}
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleDeleteCancel}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleDeleteConfirm}
            >
              삭제
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />
    </CmsPopup>
  );
};

export default BidderFormPopup;
